import { Grid, ImageList, CircularProgress, Stack, Typography, Box } from "@mui/material";
import { ProductCard } from "~components/atoms";
import { useProductsContext } from "~contexts/Products";
import { useRealtimeProductsContext } from "~contexts/RealtimeProducts";
import { authFetch } from "~helpers/authFetch";
import { addDeletedProduct } from "~helpers/deletedProductsStorage";
import { removePendingProduct } from "~helpers/addedProductsStorage";
import { useStreamContext } from "~contexts/Stream";
import { useSocketContext } from "~contexts/Socket";
import type { Product as IProduct } from "~api/stream/interfaces";
import ButtonToTop from "~components/atoms/ButtonToTop";

interface Props {
  products: IProduct[];
  page: "realtime" | "products";
  canEdit?: boolean;
}

const styles = {
  grid: {
    mt: "15px",
    overflow: "auto",
    position: "relative" as const,
    "&::-webkit-scrollbar": { display: "none", width: 0 },
  },
};

const ProductsGrid = ({ products, page, canEdit = true }: Props) => {
  const { selectedProducts, setSelectedProducts } = useRealtimeProductsContext();
  const {
    loadingProducts,
    buttonActive,
    buttons,
    setButtons,
    setProductList,
    updateCategoryCacheProducts,
    getCategoryProductsFromCache,
  } = useProductsContext();
  const { currentStream } = useStreamContext();
  const { updateStreamSocket } = useSocketContext();

  const getProductIdentifier = (product: IProduct | null) => {
    if (!product) return "";
    return String(product.id ?? product.provider?.productId ?? "");
  };

  const getKey = (product: IProduct) => product.provider?.productId || String(product.id);

  const addSelectedProduct = (product: IProduct) => {
    const key = getKey(product);
    const isAlreadySelected = selectedProducts.some((p) => getKey(p) === key);

    if (isAlreadySelected) {
      setSelectedProducts(selectedProducts.filter((p) => getKey(p) !== key));
      return;
    }

    if (selectedProducts.length >= 2) return;

    setSelectedProducts([...selectedProducts, product]);
  };

  const removeProduct = async (productRemoved: IProduct | null) => {
    if (!currentStream || !productRemoved) return;

    const streamId = currentStream.id;
    const activeButton = buttons[buttonActive];
    const categoryId = productRemoved.id_button || activeButton?.id_button;
    const productId = getProductIdentifier(productRemoved);
    if (!streamId || !categoryId || !productId) return;

    try {
      await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/stream/${streamId}/category/${categoryId}/product/${productId}`,
        method: "DELETE",
      });

      // Após exclusão bem-sucedida, atualizar apenas o front/cache sem recarregar a página
      // Atualizar lista visível (mantém filtro atual, se houver)
      const updatedProductsList = products.filter(
        (product) => getProductIdentifier(product) !== productId
      );
      setProductList(updatedProductsList);

      // Atualizar cache completo da categoria, sem depender do filtro atual
      const cachedProducts = getCategoryProductsFromCache(categoryId);
      const updatedCachedProducts = cachedProducts.filter(
        (product) => getProductIdentifier(product) !== productId
      );
      updateCategoryCacheProducts(categoryId, updatedCachedProducts);

      // Atualizar quantidade da categoria na UI (se houver)
      const updatedButtons = buttons.map((btn) =>
        btn.id_button === categoryId
          ? {
            ...btn,
            quantity:
              typeof btn.quantity === "number"
                ? Math.max(0, btn.quantity - 1)
                : btn.quantity,
          }
          : btn
      );

      setButtons(updatedButtons);

      // Cross-cleanup: se estava pendente de adição, cancelar; e registrar como deletado
      removePendingProduct(productRemoved.id!, categoryId);
      addDeletedProduct(String(productRemoved.id!), categoryId);
      updateStreamSocket({ productRemoved: { id: productRemoved.id!, id_button: categoryId } });
    } catch (error) {
      console.error("removeProduct: DELETE failed", error);
      throw new Error("Problemas na requisição para atualizar categorias");
    }
  };

  const onClickCard = (product: IProduct) => {
    if (page === "products") {
      if (!canEdit) {
        return;
      }
      // Chamar removeProduct diretamente com o produto
      removeProduct(product);
    } else {
      addSelectedProduct(product);
    }
  };

  // Mostrar loading quando está carregando E não há produtos (primeira vez, sem cache)
  if (loadingProducts && !products.length) {
    return (
      <Grid sx={styles.grid}>
        <Stack alignItems="center" justifyContent="center" sx={{ minHeight: "400px" }}>
          <CircularProgress size={48} />
          <Typography sx={{ mt: 2, color: "text.secondary" }}>
            Carregando produtos...
          </Typography>
        </Stack>
      </Grid>
    );
  }

  if (!products.length && !loadingProducts) {
    return <Grid sx={styles.grid}>Nenhum produto encontrado.</Grid>;
  }

  return (
    <Grid sx={styles.grid}>
      {loadingProducts && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1000,
            borderRadius: "8px",
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress size={48} sx={{ color: "white" }} />
            <Typography sx={{ color: "white", fontWeight: 500 }}>
              Carregando...
            </Typography>
          </Stack>
        </Box>
      )}

      <ImageList cols={5} sx={{ opacity: loadingProducts ? 0.5 : 1 }}>
        {products.map((product, index) => (
          <ProductCard
            key={`${product.provider?.productId || index}-${index}`}
            onClick={(e) => {
              if (e) {
                e.preventDefault();
                e.stopPropagation();
              }
              onClickCard(product);
            }}
            type={page === "realtime" ? "select" : "delete"}
            productName={
              product.productName || `Produto ${product.provider?.productId || index}`
            }
            productImage={
              product.images?.thumbs?.[0] ||
              product.images?.std?.[0] ||
              product.images?.hd?.[0] ||
              ""
            }
            productPrice={
              product.items?.[0]?.price
                ? `R$ ${product.items[0].price.toFixed(2)}`
                : "Preço não disponível"
            }
            isChecked={product.isChecked}
            isDisabled={page === "realtime" ? (selectedProducts.length >= 2 && !product.isChecked) : !canEdit}
            productId={product.id || product.provider?.productId}
            providerId={product.provider?.productId}
          />
        ))}
      </ImageList>

      <ButtonToTop id={page} />
    </Grid>
  );
};

export default ProductsGrid;
